"use client";
import { analyticEventSources, editsShape, pageSections, pageTypes, plpTypes } from "@/apiUtils/constants";
import { getSchemaMarkUpsByPageType } from "@/apiUtils/schemaMarkupUtils";
import {
  BannerType,
  HeaderContent,
  LandingPageWidget,
  TranslationData,
  VideoBannerType,
} from "@/common/lib/types";
import getProductListingProps from "@/pageUtils.ts/plp-utils";
import { getLangFromLocale } from "@/utils/common";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import Image from "next/image";
import { Suspense } from "react";
import HeaderEvents from "../HeaderEvents";
import StructuredData from "../StructuredData";
import BannerGrid from "../common/BannerGrid/BannerGrid";
import PromotionsView from "../common/PromotionalBanner/PromotionsView";
import HeaderVariants from "../common/headerVariants/HeaderVariants";
import BenefitSection from "../home/benefitSection/BenefitSection";
import BrandCarousel from "../home/brandCarousel/BrandCarousel";
import BrandLogoGrid from "../brandLogoGrid";
import { IBrandLogoGridRaw } from "@/types/brandLogoGrid";
import HeroBanner from "../home/heroBanner/HeroBanner";
import Navbar from "../navbar/Navbar";
import PlpBannerWidget from "../plp/PlpBannerWidget";
import ProductCarousel from "../productCarousel/ProductCarousel";
import Breadcrumb, { BreadcrumbItem } from "../ui/Breadcrumb/Breadcrumb";
import CLPFindStore from "./CLPFindStore";
import SeoFooterText from "../common/SeoFooterText";
import CollectionPromotionBanner from "../plp/CollectionPromotionBanner";
import PromoBannerGroupsView from "../common/PromotionalBanner/PromoBannerGroupsView";
import PLPHeader from "../plp/PLPHeader";

interface IProps {
  headerContent: HeaderContent;
  translations?: TranslationData;
  props: Awaited<ReturnType<typeof getProductListingProps>>;
  locale: string;
  searchParams?: Record<string, any>;
}

const CLP = ({ props, headerContent, translations, locale, searchParams }: IProps) => {
  const isSmallScreen = useMediaQuery("(max-width:768px)", {
    defaultValue: false,
    initializeWithValue: true,
  });

  const staticContent = props?.payload?.staticContent;
  const isCollectionPage = props?.payload?.contentType === plpTypes.COLLECTION;
  const editsSectionShape = staticContent?.plpWidgetContents?.pageData?.editsSection?.shape;
  const vtoEnabled = staticContent?.plpWidgetContents?.enableVirtualTryOn ?? false;
  // consts
  const headerHasMedia = staticContent?.editsSection?.every((v) => Boolean(v?.media));

  const lang = getLangFromLocale(props?.locale);

  function handleGetSections(categories: LandingPageWidget[]) {
    return categories?.map((sec, i) => {
      switch (sec?.type) {
        case pageSections.HERO_BANNER: {
          return <HeroBanner key={i} heroBannerData={sec} />;
        }
        case pageSections.PROMOTIONAL_BANNER: {
          return <PromotionsView key={i} promotionalData={sec} locale={props?.locale} />;
        }
        case pageSections.PROMO_BANNER_GROUPS: {
          return (
            <PromoBannerGroupsView
              key={i}
              promotionalBannerGroupsData={sec}
              locale={props?.locale}
              translations={translations}
            />
          );
        }
        case pageSections.RECOMMENDATIONS: {
          return (
            <ProductCarousel
              key={i}
              products={sec?.products}
              title={sec?.title}
              backgroundColor={sec?.backgroundColor}
              cta={sec?.cta}
              card={{
                promotionTile: sec?.promotionTile,
              }}
              translations={props?.payload?.common?.translations}
              tryOn={vtoEnabled}
              source={analyticEventSources.RECOMMENDED_PRODUCTS}
              isChevronVariant={!isSmallScreen}
            />
          );
        }
        case pageSections.BRAND_CAROUSEL: {
          return (
            <BrandCarousel
              lang={lang}
              key={i}
              brandsData={sec?.brands}
              title={sec?.title}
              desktopViewMode="single"
              mobileViewMode="double"
            />
          );
        }
        case pageSections.BANNER_GRID: {
          return <BannerGrid key={i} content={sec} translations={props?.payload?.common?.translations} />;
        }
        case pageSections.BRAND_LOGO_GRID_SECTION: {
          return (
            <BrandLogoGrid
              key={i}
              data={sec as unknown as IBrandLogoGridRaw}
              locale={locale}
            />
          );
        }
        case pageSections.FIND_A_STORE: {
          return (
            <CLPFindStore
              key={i}
              storeData={sec}
              translations={props?.payload?.common?.translations}
              locale={locale}
            />
          );
        }
        case pageSections.PAGE_SECTION: {
          return (
            <section className="flex flex-col" key={i}>
              <div
                key={`category_${i}_${sec?.title}`}
                className="mx-auto flex w-full flex-col gap-4 md:gap-8"
              >
                <div className="container px-4 md:px-12">
                  <div className="mx-auto flex h-[2.1875rem] w-full items-center justify-center bg-primary-2 md:h-[3.438rem]">
                    <h2 className="text-[1.125rem] font-semibold md:text-[1.375rem]">
                      {sec?.title}
                    </h2>
                  </div>
                </div>
                <div className="flex flex-col gap-8 md:gap-16 lg:gap-[72px]">
                  {handleGetSections(sec?.sections)}
                </div>
              </div>
            </section>
          );
        }
      }
    });
  }

  const breadcrumbItems: BreadcrumbItem[] | undefined = staticContent?.breadCrumbs
    ?.filter((item) => Boolean(item?.label))
    .map((item) => ({
      label: item?.label as string,
      path: item?.href || undefined,
    }));

  const schemaMarkups = getSchemaMarkUpsByPageType({
    pageType: pageTypes.CLP,
    breadCrumbs: staticContent?.breadCrumbs || [],
    locale: props.locale,
  });

  return (
    <>
      {schemaMarkups.length > 0 && <StructuredData schemaMarkups={schemaMarkups} />}
      <Suspense fallback={<></>}>
        <HeaderEvents
          pageType={pageTypes.CLP}
          pageCategory={props?.payload?.productListingPageData?.name || ""}
        />
      </Suspense>
      <Navbar
        headerContent={headerContent}
        translation={translations?.common ?? {}}
        enableVto={vtoEnabled}
        dynamicBanner={props?.payload?.staticContent?.plpWidgetContents?.pageData?.dynamicBanner}
        socialMediaIcons={props.payload?.common.socialMediaIcons ?? []}
      />
      <section className="flex flex-col">
        <div
          id="breadcrumb-section"
          className="sticky z-[15] bg-white transition-[top] duration-200 ease-in-out"
        >
          {breadcrumbItems && breadcrumbItems.length > 0 && (
            <Breadcrumb className="px-3 md:px-12 py-2" items={breadcrumbItems} />
          )}
        </div>
        {isCollectionPage && <CollectionPromotionBanner promotions={staticContent?.collectionPromotions} />}
        <section className="d:mb-9 mb-6 flex flex-col items-center gap-5 md:gap-9">
          {staticContent?.plpWidgetContents?.pageData?.brandLogo ? (
            <Image
              src={staticContent.plpWidgetContents.pageData.brandLogo}
              alt={staticContent?.title || ""}
              width={200}
              height={100}
              className="h-10 w-auto object-contain md:h-14"
            />
          ) : (
            <h1 className="text-[18px] font-semibold md:text-[1.375rem]">
              {staticContent?.title}
            </h1>
          )}
        </section>
        {(() => {
          const descriptionPosition =
            staticContent?.plpWidgetContents?.pageData?.descriptionPosition;
          const descriptionBelow = descriptionPosition === "below";
          const descriptionBlock = staticContent?.description ? (
            <section className="md:mb-9 mb-6 flex flex-col items-center gap-5 md:gap-9">
              <HeaderVariants
                variant="withSubheading"
                subheading={staticContent.description}
                translations={props?.payload?.common?.translations}
              />
            </section>
          ) : null;
          const bannerBlock = staticContent?.plpWidgetContents?.pageData?.bannerSection?.type ? (
            <section className="mb-3 md:mb-4">
              <PlpBannerWidget
                content={
                  staticContent?.plpWidgetContents?.pageData?.bannerSection as
                    | BannerType
                    | VideoBannerType
                }
                locale={props?.locale}
              />
            </section>
          ) : null;
          return descriptionBelow ? (
            <>
              {bannerBlock}
              {descriptionBlock}
            </>
          ) : (
            <>
              {descriptionBlock}
              {bannerBlock}
            </>
          );
        })()}

        {Array.isArray(staticContent?.editsSection) &&
          (staticContent?.editsSection?.length ?? 0) > 0 && (
            <div
              id="plp-edit-section"
              className="sticky z-10 bg-white mt-4 transition-[top] duration-500 ease-in-out md:mt-6 mb-8 md:mb-16 lg:mb-[72px]"
            >
              {editsSectionShape === editsShape.SQUARE ? (
                <PLPHeader
                  variant="withImageGrid"
                  tabs={staticContent?.editsSection?.map((v) => ({
                    src: v?.media ?? "",
                    label: v?.title,
                    alt: v?.title,
                    url: v?.link?.url ?? "/",
                  }))}
                />
              ) : editsSectionShape === editsShape.ROUND ? (
                <PLPHeader
                  variant="withIcons"
                  tabs={staticContent?.editsSection?.map((v) => ({
                    src: v?.media ?? "",
                    label: v?.title,
                    alt: v?.title,
                    url: v?.link?.url ?? "/",
                  }))}
                  background={staticContent?.plpWidgetContents?.pageData?.editsSection?.background}
                />
              ) : (
                <PLPHeader
                  variant="withTabs"
                  tabs={staticContent?.editsSection?.map((v) => ({
                    src: v?.media ?? "",
                    label: v?.title,
                    alt: v?.title,
                    url: v?.link?.url ?? "/",
                  }))}
                />
              )}
            </div>
          )}

        {props?.payload?.landingPageWidgets && props?.payload?.landingPageWidgets.length > 0 ? (
          <section className="flex flex-col gap-8 md:gap-16 lg:gap-[72px]">
            {handleGetSections(props?.payload?.landingPageWidgets)}
          </section>
        ) : (
          <></>
        )}

        {staticContent?.seoFooterText && (
          <section className="my-9">
            <SeoFooterText
              title={staticContent?.seoFooterText.title}
              description={staticContent?.seoFooterText.description}
              accordionContent={staticContent?.seoFooterText?.collapseSection}
              translations={props?.payload?.common?.translations}
            />
          </section>
        )}

        <section className="mt-16 md:mt-24">
          {staticContent?.plpWidgetContents?.benefits && (
            <BenefitSection benefitsData={staticContent?.plpWidgetContents?.benefits} />
          )}
        </section>
      </section>
    </>
  );
};

export default CLP;
